import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Users, User } from 'lucide-react';
import contactService from '../../services/contactService';
import { teamService } from '../../services/teamService';
import { calendarService } from '../../services/calendarService';
import { useLanguage } from '../../i18n';

// ── Shared people picker ────────────────────────────────────────────────────
// The ONE live-search guest field behind both calendar invite surfaces:
//   • #271 Manual Event Creation — the "People" field in the manual event form,
// where picks are STAGED and dispatched when the user hits "Save and Invite".
//   • #272 Upcoming Events Box   — the "Invite" search inside the upcoming-event
// detail pop-up, where a pick is invited IMMEDIATELY.
// The difference is entirely the parent's `onSelect` handler; the search, the
// data it searches, and the chips look and behave identically on both surfaces
// (the card for #272 asks for the same auto-suggest over Network Contacts and
// Teams that #271 specifies).
//
// SCOPE — deliberately Network + Teams only:
//   • a NETWORK contact is someone with a completed mutual handshake
//     (tier === 'network', backed by a real ContactLink) and therefore a
// resolvable Omnis user to invite. A rolodex card holds no account.
//   • a TEAM is expanded to its members at send time (resolveInviteeUserIds),
// not here — the roster is one extra request per team and is only needed
// when an invitation is actually dispatched.

// `tr` is the i18n `t()` function — see the teamEntry note below.
const contactEntry = (c, tr) => ({
    key: `contact:${c.id}`,
    kind: 'contact',
    id: c.id,
    userId: c.omnis_user_id,
    name: c.name || c.omnis_user?.email || tr('caleContactWord', 'Contact'),
    subtitle: c.omnis_user?.email || c.email || '',
});

// `tr` is the i18n `t()` function — teamEntry is a plain helper (not a React
// component/hook), so the caller passes it in from a component's own
// `useLanguage()` call.
const teamEntry = (team, tr) => ({
    key: `team:${team.id}`,
    kind: 'team',
    id: team.id,
    name: team.name,
    memberCount: team.member_count ?? null,
    subtitle: team.member_count != null
        ? tr('caleTeamMemberCount', 'Team · {n} member{s}', { n: team.member_count, s: team.member_count === 1 ? '' : 's' })
        : tr('caleTeamWord', 'Team'),
});

/**
 * Expand a picker selection into the concrete Omnis user ids to invite.
 * Teams are resolved to their member rosters here (one request each) and the
 * whole list is de-duplicated, so picking both a team and one of its members
 * still produces a single invitation for that person.
 */
export const resolveInviteeUserIds = async (selection) => {
    const ids = [];
    const seen = new Set();
    for (const entry of selection || []) {
        if (entry.kind === 'team') {
            try {
                const res = await teamService.getTeam(entry.id);
                for (const m of (res?.team?.members || [])) {
                    if (m.user_id && !seen.has(m.user_id)) { seen.add(m.user_id); ids.push(m.user_id); }
                }
            } catch (_) { /* a team we can't read contributes nobody */ }
        } else if (entry.userId && !seen.has(entry.userId)) {
            seen.add(entry.userId);
            ids.push(entry.userId);
        }
    }
    return ids;
};

/**
 * Send one invitation per user id, reusing the same endpoint the manual Invite
 * dialog has always used (so Bell rows, the Internal-Secretary conflict heads-up
 * and the dual-write on accept all behave identically).
 *
 * The two benign BE refusals are absorbed rather than reported as failures:
 *   • 409 — already invited to this event (re-picking a guest, or a team whose
 * member was invited individually)
 *   • 400 — "you can't invite yourself" (the actor is a member of their own team)
 * Both mean "that guest is already covered", which is not an error the user can
 * act on. Anything else is surfaced.
 */
export const inviteUsersToEvent = async (eventId, userIds) => {
    let invited = 0;
    let skipped = 0;
    const failed = [];
    for (const userId of userIds || []) {
        try {
            await calendarService.inviteToEvent(eventId, { invitee_user_id: userId });
            invited += 1;
        } catch (err) {
            if (err?.status === 409 || err?.status === 400) skipped += 1;
            else failed.push(userId);
        }
    }
    return { invited, skipped, failed };
};

export const PeoplePicker = ({
    selected = [],
    onSelect,
    onRemove = null,
    placeholder = null,
    testId = 'people-picker',
    autoFocus = false,
    busy = false,
}) => {
    const { t } = useLanguage();
    const effectivePlaceholder = placeholder ?? t('calePeoplePickerPlaceholder', 'Search contacts or teams…');
    const [contacts, setContacts] = useState([]);
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    // Load the whole searchable graph ONCE — the Network and the team list are
    // both small, bounded sets, so filtering client-side gives a true
    // as-you-type suggestion list with no per-keystroke round trip.
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.allSettled([contactService.listContacts({}), teamService.listTeams()])
            .then(([contactRes, teamRes]) => {
                if (cancelled) return;
                const all = contactRes.status === 'fulfilled' ? (contactRes.value?.contacts || []) : [];
                setContacts(all
                    .filter((c) => c.tier === 'network' && c.omnis_user_id)
                    .map((c) => contactEntry(c, t)));
                setTeams((teamRes.status === 'fulfilled' ? (teamRes.value?.teams || []) : []).map((tm) => teamEntry(tm, t)));
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // Deliberately NOT re-fetching contacts/teams on a language switch: `t` is
        // only used to label the fetched rows, and refetching here would cost a real
        // network round trip for a cosmetic re-translation. The picker is transient
        // (mounted per dialog open), so a stale label survives at most one open.
        // NOTE: the directive must be the LAST comment line — a multi-line
        // continuation makes "next line" the following comment, not the dep array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close the suggestion list on an outside click (a blur handler would fire
    // before the click on a suggestion lands).
    useEffect(() => {
        if (!open) return undefined;
        const onDocClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const selectedKeys = useMemo(() => new Set((selected || []).map((s) => s.key)), [selected]);

    const suggestions = useMemo(() => {
        const q = query.trim().toLowerCase();
        const pool = [...contacts, ...teams].filter((e) => !selectedKeys.has(e.key));
        if (!q) return pool;
        return pool.filter((e) => `${e.name} ${e.subtitle}`.toLowerCase().includes(q));
    }, [contacts, teams, query, selectedKeys]);

    const pick = (entry) => {
        setQuery('');
        setOpen(false);
        onSelect?.(entry);
    };

    return (
        <div ref={rootRef} data-testid={testId} className="relative">
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                    {selected.map((entry) => (
                        <span
                            key={entry.key}
                            data-testid={`${testId}-chip`}
                            data-kind={entry.kind}
                            className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-full text-xs bg-[rgba(74,144,240,0.12)] text-pai-blue border border-indigo-200 dark:border-indigo-800"
                        >
                            {entry.kind === 'team' ? <Users size={11} className="flex-shrink-0" /> : <User size={11} className="flex-shrink-0" />}
                            <span className="truncate">{entry.name}</span>
                            {onRemove && (
                                <button
                                    type="button"
                                    data-testid={`${testId}-chip-remove`}
                                    aria-label={t('caleRemoveGuest', 'Remove {name}', { name: entry.name })}
                                    onClick={() => onRemove(entry)}
                                    className="flex-shrink-0 text-pai-blue hover:text-rose-600 dark:hover:text-rose-400"
                                >
                                    <X size={11} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}

            <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-pai-faint pointer-events-none" />
                <input
                    type="text"
                    data-testid={`${testId}-input`}
                    autoFocus={autoFocus}
                    disabled={busy}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={(e) => {
                        // Never let the search field submit the surrounding form —
                        // Enter picks the single obvious match instead.
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (suggestions.length === 1) pick(suggestions[0]);
                        } else if (e.key === 'Escape') {
                            setOpen(false);
                        }
                    }}
                    placeholder={effectivePlaceholder}
                    className="w-full pl-8 pr-2 py-1.5 text-sm rounded border border-pai bg-white/[0.03] text-white placeholder-gray-400 focus:outline-none focus:ring-1 disabled:opacity-60"
                />
            </div>

            {open && (
                <div
                    data-testid={`${testId}-suggestions`}
                    className="absolute z-30 left-0 right-0 mt-1 max-h-44 overflow-y-auto rounded-lg border border-pai-subtle bg-white/[0.03] shadow-modal-sm"
                >
                    {loading ? (
                        <p className="px-3 py-2 text-xs text-pai-meta">{t('caleLoadingYourNetwork', 'Loading your network…')}</p>
                    ) : suggestions.length === 0 ? (
                        <p data-testid={`${testId}-empty`} className="px-3 py-2 text-xs text-pai-meta">
                            {contacts.length + teams.length === 0
                                ? t('calePickerEmptyNoContacts', 'No network contacts or teams yet — connect with someone in Contacts first.')
                                : t('calePickerNoMatch', 'No contact or team matches “{query}”.', { query })}
                        </p>
                    ) : (
                        <ul className="py-1">
                            {suggestions.map((entry) => (
                                <li key={entry.key}>
                                    <button
                                        type="button"
                                        data-testid={`${testId}-option`}
                                        data-kind={entry.kind}
                                        onClick={() => pick(entry)}
                                        className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 hover:bg-white/[0.07]"
                                    >
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            entry.kind === 'team'
                                                ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
                                                : 'bg-[rgba(74,144,240,0.16)] text-pai-blue'
                                        }`}>
                                            {entry.kind === 'team' ? <Users size={12} /> : <User size={12} />}
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-sm text-white truncate">{entry.name}</span>
                                            {entry.subtitle && (
                                                <span className="block text-[11px] text-pai-meta truncate">{entry.subtitle}</span>
                                            )}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default PeoplePicker;
